import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, Inject, Input, OnDestroy, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { Environment } from 'src/environments/environment';
import { AuthService } from '../services/auth.service';
import { Storage } from '@capacitor/storage';
import { Capacitor } from '@capacitor/core';

@Directive({
  selector: 'img, ion-img'
})
export class AuthImageDirective implements OnInit, OnDestroy {
  @Input() src!: string;

  private supportsServiceWorker = !Capacitor.isNativePlatform() && 'serviceWorker' in navigator;
  private observer?: IntersectionObserver;

  constructor(
    private el: ElementRef<HTMLImageElement | HTMLAudioElement>,
    private renderer: Renderer2,
    private authService: AuthService,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit() {
    // If service workers are supported, let them handle it
    // Or if the image does not start with CONTENT_URL + media
    if (this.supportsServiceWorker || !this.src.startsWith(Environment.CONTENT_URL + '/media')) {
      // Just set the src normally - service worker will intercept
      const img = this.el.nativeElement;
      this.renderer.setAttribute(img, 'src', this.src);
      return;
    }

    // For iOS/platforms without service worker support, handle manually
    this.setupAuthenticatedImageLoading(this.src);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const source = this.src || changes['src']?.currentValue;
    if (this.supportsServiceWorker || !source.startsWith(Environment.CONTENT_URL + '/media')) {
      // Just set the src normally - service worker will intercept
      const img = this.el.nativeElement;
      this.renderer.setAttribute(img, 'src', this.src);
      return;
    }
    if (source) {
      this.setupAuthenticatedImageLoading(source);
    }
  }

  private setupAuthenticatedImageLoading(source: string) {
    const img = this.el.nativeElement;

    // Set up intersection observer for lazy loading
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadAuthenticatedImage(source);
          this.observer?.unobserve(img);
        }
      });
    });

    this.observer.observe(img);
  }

  private async loadAuthenticatedImage(source: string) {
    const img = this.el.nativeElement;

    try {
      this.renderer.addClass(img, 'auth-img-loading');
      const sourceUrl = source || this.src;

      // Check cache first
      const networkImage = this.getNetworkImage(sourceUrl);
      const cachedImage = await this.getCachedImage(sourceUrl);
      if (cachedImage) {
        this.renderer.setAttribute(img, 'src', cachedImage);
        this.renderer.removeClass(img, 'auth-img-loading');
        this.renderer.addClass(img, 'auth-img-loaded');
        return;
      }

      await networkImage.then((objectUrl) => {
        this.renderer.setAttribute(img, 'src', objectUrl);
        this.renderer.removeClass(img, 'auth-img-loading');
        this.renderer.addClass(img, 'auth-img-loaded');
      });

      img.onload = () => {
        // Keep the objectUrl for now, clean up on component destroy
      };

    } catch (error) {
      console.error('Failed to load authenticated image:', this.src, error);
      this.renderer.removeClass(img, 'auth-img-loading');
    }
  }

  private async getNetworkImage(sourceUrl: string) {
    const token = await this.authService.getToken();

    // Fetch image with auth headers
    const response = await fetch(sourceUrl, {
      headers: token ? {
        'Authorization': `Bearer ${token}`
      } : {},
      mode: 'cors',
      credentials: 'include'
    });

    if (response.ok) {
      // Convert blob to base64
      const blob = await response.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result);
        };
        reader.onerror = () => reject(new Error('Failed to convert blob to base64'));
        reader.readAsDataURL(blob);
      });

      // Cache the image for offline use
      this.cacheImage(sourceUrl, base64);
      return base64;

    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  private async getCachedImage(sourceUrl: string): Promise<string | null> {
    try {
      const response = (await Storage.get({ key: `auth-image-${sourceUrl}` }).catch(() => null))?.value;
      if (response) {
        return response;
      }

    } catch (error) {
      console.warn('Cache retrieval failed:', error);
    }

    return null;
  }

  private async cacheImage(url: string, objectUrl: string) {
    try {
      await Storage.set({ key: `auth-image-${url}`, value: objectUrl });
    } catch (error) {
      console.warn('Image caching failed:', error);
    }
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    // Clean up object URLs to prevent memory leaks
    const img = this.el.nativeElement;
    const src = img.src;
    if (src && src.startsWith('blob:')) {
      URL.revokeObjectURL(src);

    }
  }
}